import useQuickAction from "../../hooks/useQuickAction";
import { useState } from "react";
import AddSalaryTemplateModal from "../../components/salaryTemplates/AddSalaryTemplateModal";
import { Button } from "../../components/ui";
import useGetSalaryTemplates from "../../hooks/salaryTemplates/useGetSalaryTemplates";
import { BsThreeDots } from "react-icons/bs";
import Dropdown from "../../components/dropdown/Dropdown";
import EditSalaryTemplateModal from "../../components/salaryTemplates/EditSalaryTemplateModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { useDeleteSalaryTemplate } from "../../hooks/salaryTemplates/useDeleteSalaryTemplate";
import { Link } from "react-router-dom";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import useDuplicateSalaryTemplate from "../../hooks/salaryTemplates/useDuplicateSalaryTemplate";
import Pagination from "../../components/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import RoundedContainer from "../../components/containers/RoundedContainer";

const breadcrumbs = [
  {
    label: "Salary Templates",
    link: "",
  },
];

const SalaryTemplatesList = () => {
  const { templatesList, isTemplateListLoading, pagination, handlePageClick } =
    useGetSalaryTemplates();

  const [currentOpenDropdownId, setCurrentOpenDropdownId] = useState(null);

  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  useQuickAction("add-salary-template", () => setShowAddTemplateModal(true));
  const toggleTemplateModal = () => {
    setShowAddTemplateModal((prev) => !prev);
  };

  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);

  const toggleEditTemplateModal = (template) => {
    setTemplateToEdit(template);
    setShowEditTemplateModal((prev) => !prev);
  };

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const { deleteTemplate, isDeleting } = useDeleteSalaryTemplate({
    onDelete: () => {
      toggleDeleteConfirmation();
    },
  });

  const toggleDeleteConfirmation = (template) => {
    setShowDeleteConfirmation((prev) => !prev);
    setTemplateToDelete(template);
  };

  const [showDuplicateConfirmation, setShowDuplicateConfirmation] =
    useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState(null);

  const { isDuplicating, duplicateTemplate } = useDuplicateSalaryTemplate({
    onDuplicate: () => {
      setShowDuplicateConfirmation(false);
      setTemplateToDuplicate(null);
    },
  });

  const toggleDuplicateConfirmation = (template) => {
    setShowDuplicateConfirmation((prev) => !prev);
    setTemplateToDuplicate(template);
  };

  return (
    <>
      <AddSalaryTemplateModal
        isOpen={showAddTemplateModal}
        toggleModal={toggleTemplateModal}
      />
      <EditSalaryTemplateModal
        isOpen={showEditTemplateModal}
        templateToEdit={templateToEdit}
        toggleModal={toggleEditTemplateModal}
      />
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={toggleDeleteConfirmation}
        title={"Confirm Delete"}
        description={`Are you sure you want to delete '${templateToDelete?.name}' template?`}
        confirmButtonLabel={"Delete"}
        onConfirm={() => deleteTemplate(templateToDelete?.template_id)}
        isLoading={isDeleting}
        loadingMessage="Deleting template..."
      />
      <ConfirmationModal
        isOpen={showDuplicateConfirmation}
        onClose={toggleDuplicateConfirmation}
        title={"Confirm Duplicate"}
        description={`Are you sure you want to duplicate '${templateToDuplicate?.name}' template?`}
        confirmButtonLabel={"Duplicate"}
        onConfirm={() =>
          duplicateTemplate(
            templateToDuplicate?.template_id,
            templateToDuplicate
          )
        }
        isLoading={isDuplicating}
        loadingMessage="Duplicating template..."
      />

      <div className="px-4 pt-5">
        <BreadCrumbs crumbs={breadcrumbs} />

        <div className=" flex items-center justify-end">
          <Button onClick={toggleTemplateModal}>Add new template</Button>
        </div>

        <div>
          <RoundedContainer>
            {isTemplateListLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="loader"></div>
                <p className="ml-6">Loading templates...</p>
              </div>
            ) : !templatesList?.length ? (
              <div className="text-center py-8">
                <p className=" text-gray-700 text-3xl">No Salary Templates To Show</p>
              </div>
            ) : (
              <Table className="w-full mb-5 table-fixed">
                <TableHead>
                  <TableHeaderCell className="w-4/6">
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/6 text-center">
                    Manage
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/6 text-center">
                    Actions
                  </TableHeaderCell>
                </TableHead>
                <TableBody>
                  {templatesList?.map((el) => {
                    const { template_id, name } = el;
                    return (
                      <TableRow key={template_id}>
                        <TableCell className="text-dark font-medium text-sm">
                          {name}
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm">
                          <Link
                            to={
                              "/salary-templates/manage-salary-template/" +
                              template_id
                            }
                            className="text-primary font-medium hover:underline"
                          >
                            Manage
                          </Link>
                        </TableCell>
                        <TableCell className="text-center text-dark font-medium text-sm">
                          <div className="relative">
                            <Button
                              variant={"ghost"}
                              size={"icon"}
                              className="mx-auto grid place-items-center"
                              onClick={() =>
                                setCurrentOpenDropdownId(
                                  currentOpenDropdownId ? null : template_id
                                )
                              }
                            >
                              <BsThreeDots className=" text-gray-600 w-6 h-6 hover:text-blue-600" />
                            </Button>
                            {template_id === currentOpenDropdownId ? (
                              <Dropdown
                                show={template_id === currentOpenDropdownId}
                                className={
                                  "top-6 right-0 w-[9rem] bg-white rounded-l z-10 shadow-lg"
                                }
                                setShow={() =>
                                  setCurrentOpenDropdownId(template_id)
                                }
                              >
                                <ul className="">
                                  <li className="hover:bg-gray-100">
                                    <Button
                                      variant={"ghost"}
                                      onClick={() => toggleEditTemplateModal(el)}
                                    >
                                      Edit
                                    </Button>
                                  </li>
                                  <li className="hover:bg-gray-100">
                                    <Button
                                      variant={"ghost"}
                                      onClick={() =>
                                        toggleDuplicateConfirmation(el)
                                      }
                                    >
                                      Duplicate
                                    </Button>
                                  </li>
                                  <li className="hover:bg-gray-100">
                                    <Button
                                      variant={"ghost"}
                                      onClick={() => toggleDeleteConfirmation(el)}
                                    >
                                      Delete
                                    </Button>
                                  </li>
                                </ul>
                              </Dropdown>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </RoundedContainer>

          <RoundedContainer>
            <Pagination
              currentPage={pagination.current_page_number}
              lengthofItems={+pagination.total_count}
              limit={pagination.per_page_limit}
              onPageChange={handlePageClick}
            />
          </RoundedContainer>
        </div>
      </div>
    </>
  );
};

export default SalaryTemplatesList;
